import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormToggleSection,
} from '@shigo-ui/dashboard-ui';
import {
  DateRangePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shigo-ui/ui';

import {
  dealAuctionTypeLabel,
  DealAuctionType,
  DealFrequencyUnit,
  type DealFormValue,
} from '../../model';

export function DealGeneralFields() {
  'use no memo';
  const { register, control, setValue, formState } = useFormContext<DealFormValue>();
  const auctionType = useWatch({ control, name: 'auctionType' });
  const frequencyTimeUnit = useWatch({
    control,
    name: 'frequencyCapping.fcTimeUnit',
  });
  const startDate = useWatch({ control, name: 'startDate' });
  const endDate = useWatch({ control, name: 'endDate' });

  return (
    <DashboardPageFormBody
      width="lg"
      title="General"
      description="Configure deal identity, flight dates, pricing, buyer access, and delivery limits."
    >
      <div className="grid gap-5 md:grid-cols-2">
        <DashboardPageFormField
          label="Name"
          required
          htmlFor="deal-name"
          hint="Internal name used to identify this deal."
          error={formState.errors.name?.message}
        >
          <Input id="deal-name" {...register('name')} />
        </DashboardPageFormField>
        <DashboardPageFormField
          label="Deal ID"
          required
          htmlFor="deal-id"
          hint="Identifier buyers use when bidding on this deal."
          error={formState.errors.dealId?.message}
        >
          <Input id="deal-id" {...register('dealId')} />
        </DashboardPageFormField>
        <DashboardPageFormField
          label="Date range"
          hint="Clear the range to keep the deal open-ended."
          error={formState.errors.startDate?.message ?? formState.errors.endDate?.message}
        >
          <DateRangePicker
            label="Date range"
            presets={[]}
            popoverAlign="start"
            triggerClassName="max-w-none"
            value={{ dateFrom: startDate, dateTo: endDate }}
            onChange={({ dateFrom, dateTo }) => {
              setValue('startDate', dateFrom, { shouldDirty: true, shouldValidate: true });
              setValue('endDate', dateTo, { shouldDirty: true, shouldValidate: true });
            }}
          />
        </DashboardPageFormField>
        <DashboardPageFormField
          label="Auction type"
          required
          htmlFor="deal-auction-type"
          hint="Determines how the clearing price is calculated."
          error={formState.errors.auctionType?.message}
        >
          <Select
            value={auctionType}
            onValueChange={(value) => {
              setValue('auctionType', value as DealAuctionType, { shouldDirty: true });
            }}
          >
            <SelectTrigger id="deal-auction-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(DealAuctionType).map((type) => (
                <SelectItem key={type} value={type}>
                  {dealAuctionTypeLabel(type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DashboardPageFormField>
        <DashboardPageFormField
          label="Bid floor"
          htmlFor="deal-bid-floor"
          hint="Minimum eligible bid. Leave empty when no explicit floor is required."
          error={formState.errors.bidFloor?.message}
        >
          <div className="relative">
            <span
              className="text-muted-foreground pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm"
              aria-hidden="true"
            >
              $
            </span>
            <Input
              id="deal-bid-floor"
              type="number"
              min="0"
              step="0.01"
              className="pl-7"
              {...register('bidFloor')}
            />
          </div>
        </DashboardPageFormField>
        <DashboardPageFormField
          label="Currency"
          htmlFor="deal-currency"
          hint="Deal floors are settled in USD."
        >
          <Input id="deal-currency" value="USD" disabled readOnly {...register('bidFloorCur')} />
        </DashboardPageFormField>
        <DashboardPageFormField
          label="Wseat"
          htmlFor="deal-wseat"
          hint="Optional buyer seat IDs separated by commas or new lines."
        >
          <Textarea id="deal-wseat" rows={4} {...register('wseat')} />
        </DashboardPageFormField>
        <DashboardPageFormField
          label="Wadomain"
          htmlFor="deal-wadomain"
          hint="Optional advertiser domains separated by commas or new lines."
        >
          <Textarea id="deal-wadomain" rows={4} {...register('wadomain')} />
        </DashboardPageFormField>
      </div>

      <Controller
        control={control}
        name="frequencyCapping.fcActive"
        render={({ field }) => (
          <DashboardPageFormToggleSection
            label="Frequency capping"
            description="Limit how often a user can receive impressions from this deal."
            checked={field.value}
            onCheckedChange={field.onChange}
          >
            <div className="grid gap-5 md:grid-cols-3">
              <DashboardPageFormField
                label="Limit"
                required
                htmlFor="deal-frequency-limit"
                hint="Maximum impressions during the configured duration."
                error={formState.errors.frequencyCapping?.fcLimit?.message}
              >
                <Input
                  id="deal-frequency-limit"
                  type="number"
                  min="0"
                  {...register('frequencyCapping.fcLimit')}
                />
              </DashboardPageFormField>
              <DashboardPageFormField
                label="Duration"
                required
                htmlFor="deal-frequency-duration"
                hint="Length of the frequency-cap window."
                error={formState.errors.frequencyCapping?.fcDuration?.message}
              >
                <Input
                  id="deal-frequency-duration"
                  type="number"
                  min="0"
                  {...register('frequencyCapping.fcDuration')}
                />
              </DashboardPageFormField>
              <DashboardPageFormField
                label="Time unit"
                required
                htmlFor="deal-frequency-time-unit"
                hint="Unit used for the duration."
              >
                <Select
                  value={frequencyTimeUnit}
                  onValueChange={(value) => {
                    setValue('frequencyCapping.fcTimeUnit', value as DealFrequencyUnit, {
                      shouldDirty: true,
                    });
                  }}
                >
                  <SelectTrigger id="deal-frequency-time-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DealFrequencyUnit.Hour}>Hours</SelectItem>
                    <SelectItem value={DealFrequencyUnit.Day}>Days</SelectItem>
                  </SelectContent>
                </Select>
              </DashboardPageFormField>
            </div>
          </DashboardPageFormToggleSection>
        )}
      />
    </DashboardPageFormBody>
  );
}
